import type { ApolloClient } from "@apollo/client";
import type { Dispatch } from "redux";

import { bankConstants } from "./types";
import { getAllEmployerAccounts } from "actions/employerActions";
import bankService from "services/bankService";
import ActionCreator from "utils/actionHandler";
import type { AppDispatch } from "store/configureStore";
import { pick } from "lodash";

// the slice of Plaid Link's onSuccess metadata this app forwards to the API
interface PlaidLinkMetadata {
  public_token: string;
  account_id: string;
  link_session_id: string;
  accounts: object[];
  account: object;
  institution: { name?: string; institution_id?: string };
}

function getRequiredPlaidAccountFields(account: object) {
  return pick(account, [
    "id",
    "name",
    "type",
    "subtype",
    "mask",
    "verification_status",
  ]);
}

function formatPlaidMetadata(metadata: PlaidLinkMetadata) {
  const formattedMetadata = pick(metadata, [
    "public_token",
    "account_id",
    "link_session_id",
    "accounts",
    "account",
    "institution",
  ]);

  return {
    ...formattedMetadata,
    institution: pick(formattedMetadata.institution, [
      "name",
      "institution_id",
    ]),
    account: getRequiredPlaidAccountFields(formattedMetadata.account),
    accounts: formattedMetadata.accounts.map(getRequiredPlaidAccountFields),
  };
}

export const linkBank = (
  client: ApolloClient<object>,
  public_token: string,
  metadata: PlaidLinkMetadata
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof bankService.linkBank>>
    >(bankConstants.LINK_BANK_WITH_ICON);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      bankService.linkBank(client, public_token, formatPlaidMetadata(metadata))
    );
  };
};

export const linkBankIndividual = (
  client: ApolloClient<object>,
  public_token: string,
  metadata: PlaidLinkMetadata,
  ownerName: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof bankService.linkBankIndividual>>
    >(bankConstants.LINK_BANK_WITH_ICON);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      bankService.linkBankIndividual(
        client,
        public_token,
        formatPlaidMetadata(metadata),
        ownerName
      )
    );
  };
};

export const deleteBank = (client: ApolloClient<object>, accountId: string) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<string>(bankConstants.DELETE_BANK);
    dispatch(actionCreator.request());
    return bankService
      .deleteBankIndividual(client, accountId)
      .then(() => dispatch(actionCreator.success(accountId)))
      .catch((error: Error) => dispatch(actionCreator.failure(error)));
  };
};

export const deleteEmployerBank = (
  client: ApolloClient<object>,
  accountId: string
) => {
  return (dispatch: AppDispatch) => {
    const actionCreator = new ActionCreator<string>(bankConstants.DELETE_BANK);
    dispatch(actionCreator.request());
    return bankService
      .deleteBankEmployer(client, accountId)
      .then(() => {
        const successAction = dispatch(actionCreator.success(accountId));
        // Re-fetch the authoritative employer bank list (network-only) so the
        // removed bank clears from the dashboard immediately instead of
        // lingering until a manual page refresh. Fire-and-forget: it must not
        // delay resolution of the delete promise the caller chains onto.
        void dispatch(getAllEmployerAccounts(client));
        // Return the SUCCESS action so callers chaining on this promise see the
        // dispatched action (and can inspect its `error` field), not undefined.
        return successAction;
      })
      .catch((error: Error) => dispatch(actionCreator.failure(error)));
  };
};

export const verifyBankAccount = (
  client: ApolloClient<object>,
  accountId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof bankService.verifyBankAccountRequest>>
    >(bankConstants.VERIFY_BANK);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      bankService.verifyBankAccountRequest(client, accountId)
    );
  };
};

export const getPlaidLinkToken = (
  client: ApolloClient<object>,
  bankAccountId?: string | null
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof bankService.getPlaidLinkToken>>
    >(bankConstants.GET_PLAID_LINK_TOKEN);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      bankService.getPlaidLinkToken(client, bankAccountId)
    );
  };
};

export const oAuthPlaidLinkError = (error: unknown) => {
  const actionCreator = new ActionCreator(bankConstants.OAUTH_PLAID_LINK_TOKEN);
  return actionCreator.failure(error);
};

// nothing else ever settles this key, so the error would otherwise persist
export const clearOAuthPlaidLinkError = () => ({
  type: bankConstants.OAUTH_PLAID_LINK_TOKEN_SUCCESS,
});

export const getReauthorizationLinkToken = (
  client: ApolloClient<object>,
  accountId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof bankService.getReauthorizationLinkToken>>
    >(bankConstants.GET_REAUTHORIZATION_LINK_TOKEN);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      bankService.getReauthorizationLinkToken(client, accountId)
    );
  };
};

export const updateLinkToken = (
  client: ApolloClient<object>,
  accountId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<string>(
      bankConstants.UPDATE_LINK_TOKEN
    );
    dispatch(actionCreator.request());
    return bankService
      .updateLinkToken(client, accountId)
      .then(() => dispatch(actionCreator.success(accountId)))
      .catch((error: Error) => dispatch(actionCreator.failure(error)));
  };
};

export const getIndividualReauthorizationLinkToken = (
  client: ApolloClient<object>,
  accountId: string
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<
        ReturnType<typeof bankService.getIndividualReauthorizationLinkToken>
      >
    >(bankConstants.GET_INDIVIDUAL_REAUTHORIZATION_LINK_TOKEN);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      bankService.getIndividualReauthorizationLinkToken(client, accountId)
    );
  };
};
